import { use, type ReactElement } from 'react';
import { SvgUpRight } from '@/components/icons-eataly';
import {
    getCategoryProductCountById,
    isCategoryWithoutProducts,
    type CategoryProductCounts,
} from '@/lib/category-product-counts';
import type { SuggestionItem } from './types';
import { highlightMatch } from './utils/highlight-match';
import { RowButton } from './components/row-button';

function filterCategorySuggestions(
    items: SuggestionItem[],
    productCountById: Map<string, number> | undefined
): SuggestionItem[] {
    return items.filter((item) => !isCategoryWithoutProducts(productCountById, item.id));
}

/**
 * Category column of the search suggestions panel.
 * Suspends on the counts promise; render inside `<Suspense fallback={null}>`.
 */
export function CategorySuggestionColumn({
    items,
    countsPromise,
    trimmedQuery,
    onNavigate,
}: {
    items: SuggestionItem[];
    countsPromise: Promise<CategoryProductCounts | undefined>;
    trimmedQuery: string;
    onNavigate: (link: string) => void;
}): ReactElement | null {
    const counts = use(countsPromise);
    const visible = filterCategorySuggestions(items, getCategoryProductCountById(counts));
    if (visible.length === 0) {
        return null;
    }

    return (
        <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="search-category-suggestions">
            {visible.map((item) => (
                <li key={`category-${item.id}`} className="w-full list-none">
                    <RowButton onMouseDown={(e) => e.preventDefault()} onClick={() => onNavigate(item.link ?? '')}>
                        <SvgUpRight className="size-5 shrink-0" aria-hidden />
                        <p className="flex-1 text-left text-[length:var(--font-size-200)] leading-[var(--line-height-200)] tracking-[0.14px] text-[color:var(--neutral-10)]">
                            {highlightMatch(item.label, trimmedQuery)}
                        </p>
                    </RowButton>
                </li>
            ))}
        </ul>
    );
}
